import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Button,
  Alert,
  Box,
  Checkbox,
  Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PropertyForm } from './PropertyForm'
import { useCreateProperty } from '../hooks/useProperties'
import type { PropertyFormData } from '../validation'
import { useAgency } from '@/features/agencies/hooks/useAgency'
import { useSubscription } from '@/shared/hooks/useSubscription'
import { UpgradeModal } from '@/shared/components/UpgradeModal'
import { useState } from 'react'
import { AlertTriangle } from 'lucide-react'
import { useGlobalAccountants } from '@/features/users/hooks/useGlobalAccountants'

interface PropertyCreateModalProps {
  isOpen: boolean
  onClose: () => void
}

export function PropertyCreateModal({ isOpen, onClose }: PropertyCreateModalProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createProperty = useCreateProperty()
  const { data: agency } = useAgency()
  const { data: globalAccountants = [] } = useGlobalAccountants(agency?.id)
  const { canCreateResidence, usage, limits, isNearLimit } = useSubscription()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [assignGlobalAccountants, setAssignGlobalAccountants] = useState(true)

  const handleSubmit = async (data: PropertyFormData) => {
    if (!agency?.id) {
      console.error('Agency ID not found')
      alert(t('common.error'))
      return
    }

    // Vérifier la limite avant de créer
    if (!canCreateResidence()) {
      setShowUpgradeModal(true)
      return
    }

    try {
      const newProperty = await createProperty.mutateAsync({
        ...data,
        agency_id: agency.id,
        assignGlobalAccountants, // Passer l'option au hook
      })
      onClose()
      navigate(`/properties/${newProperty.id}`)
    } catch (error) {
      console.error('Error creating property:', error)
      alert(t('common.error'))
    }
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="fixed"
        inset="0"
      >
        <DialogContent margin="auto" maxWidth="600px" maxHeight="85vh" width="full">
          <DialogHeader>
            <DialogTitle>{t('properties.create')}</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody pb={6} overflowY="auto" maxHeight="calc(85vh - 180px)">
            {/* Afficher warning si proche de la limite */}
            {usage && limits && limits.residences !== -1 && isNearLimit('residences') && (
              <Alert.Root status="warning" mb={4}>
                <Alert.Indicator>
                  <AlertTriangle />
                </Alert.Indicator>
                <Alert.Content>
                  <Alert.Title>Attention</Alert.Title>
                  <Alert.Description>
                    Vous avez utilisé {usage.residences}/{limits.residences} résidences. Pensez à
                    mettre à niveau votre formule.
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            )}

            <PropertyForm
              onSubmit={handleSubmit}
              onCancel={onClose}
              isLoading={createProperty.isPending}
              showActions={false}
              formId="property-create-form"
            />

            {/* Option d'association des comptables globaux - uniquement si des comptables globaux existent */}
            {globalAccountants.length > 0 && (
              <Box mt={4} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                <Checkbox.Root
                  checked={assignGlobalAccountants}
                  onCheckedChange={(e) => setAssignGlobalAccountants(!!e.checked)}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>
                    <Text fontWeight="medium">Associer les comptables globaux à cette résidence</Text>
                  </Checkbox.Label>
                </Checkbox.Root>
                <Text fontSize="sm" color="gray.600" mt={1} ml={6}>
                  Comptables concernés : {globalAccountants.map((a) => `${a.first_name} ${a.last_name}`).join(', ')}
                </Text>
              </Box>
            )}
          </DialogBody>
          <DialogFooter
            position="sticky"
            bottom={0}
            bg="white"
            borderTopWidth="1px"
            display="flex"
            gap={3}
            justifyContent="flex-end"
            p={4}
          >
            <Button variant="outline" onClick={onClose} disabled={createProperty.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="property-create-form"
              colorPalette="brand"
              loading={createProperty.isPending}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>

      {/* Modal d'upgrade si limite atteinte */}
      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        reason={`Vous avez atteint la limite de ${limits?.residences} résidences de votre formule actuelle.`}
        resource="residences"
      />
    </DialogRoot>
  )
}
